import React from "react";
import { graphql } from "gatsby";
import { motion, useInView } from "framer-motion";
import { PrismicRichText } from "@prismicio/react";

import PhoneCarousel from "../../components/PhoneCarousel";
import SocialVideo from "../../components/SocialVideo";
import Icon from "../../components/Icon";
import { ActiveSlideProvider } from "../../context/active-slide-context";

import * as styles from "./social-videos.module.css";

const INITIAL_COMMENTS = 3020;
const INITIAL_LIKES = 343904;

const StatsWidget = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [comments, setComments] = React.useState(INITIAL_COMMENTS);
  const [likes, setLikes] = React.useState(INITIAL_LIKES);
  const [commentFlash, setCommentFlash] = React.useState(false);
  const [likeFlash, setLikeFlash] = React.useState(false);

  // Random like increments (only when in view)
  React.useEffect(() => {
    if (!isInView) return;
    const scheduleNextLike = () => {
      const delay = 1000 + Math.random() * 2000; // 1-3 seconds
      return setTimeout(() => {
        setLikeFlash(true);
        setTimeout(() => setLikeFlash(false), 300);
        setLikes(prev => prev + 1);
        likeTimer = scheduleNextLike();
      }, delay);
    };
    let likeTimer = scheduleNextLike();
    return () => clearTimeout(likeTimer);
  }, [isInView]);

  // Random comment increments (only when in view)
  React.useEffect(() => {
    if (!isInView) return;
    const scheduleNextComment = () => {
      const delay = 2000 + Math.random() * 4000; // 2-6 seconds
      return setTimeout(() => {
        setCommentFlash(true);
        setTimeout(() => setCommentFlash(false), 300);
        setComments(prev => prev + 1);
        commentTimer = scheduleNextComment();
      }, delay);
    };
    let commentTimer = scheduleNextComment();
    return () => clearTimeout(commentTimer);
  }, [isInView]);

  const formatNumber = num => {
    return num.toLocaleString();
  };

  return (
    <div ref={ref} className={styles.statsWidget}>
      <motion.span
        animate={{
          scale: commentFlash ? 1.3 : 1,
          color: commentFlash
            ? "var(--color-bright-blue)"
            : "var(--color-grey)",
        }}
        transition={{ duration: 0.15 }}
      >
        <Icon name="speechBubble" size="1.5em" />
      </motion.span>
      <span>{formatNumber(comments)}</span>
      <motion.span
        animate={{
          scale: likeFlash ? 1.3 : 1,
          color: likeFlash ? "var(--color-spice-red)" : "var(--color-grey)",
        }}
        transition={{ duration: 0.15 }}
      >
        <Icon name="heart" size="1.5em" />
      </motion.span>
      <span>{formatNumber(likes)}</span>
    </div>
  );
};

export const SocialVideos = ({ slice }) => {
  const { heading, copy } = slice?.primary || {};

  return (
    <section
      data-color-scheme="black"
      data-highlight-background="mellow-yellow"
      data-highlight-foreground="black"
      data-tilt-scheme="right"
      id="content"
    >
      <div className={"page-width padding-section-vertical " + styles.inner}>
        <motion.div
          className={styles.copy}
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {heading && (
            <PrismicRichText
              field={heading.richText}
              components={{
                heading2: ({ children }) => <h2 className="h1">{children}</h2>,
                strong: ({ children }) => <small>{children}</small>,
              }}
            />
          )}
          {copy && <PrismicRichText field={copy.richText} />}
        </motion.div>
        <motion.div
          className={styles.carousel}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <ActiveSlideProvider>
            <PhoneCarousel>
              {slice.items?.map(
                ({ video }, index) =>
                  video && (
                    <SocialVideo key={video.id} index={index} url={video.url} />
                  )
              )}
            </PhoneCarousel>
            <StatsWidget />
          </ActiveSlideProvider>
        </motion.div>
      </div>
    </section>
  );
};

export const query = graphql`
  fragment HomepageDataBodySocialVideos on PrismicHomepageDataBodySocialVideos {
    primary {
      heading {
        richText
      }
      copy {
        richText
      }
    }
    items {
      video {
        id
        url
      }
    }
  }
`;
